import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import isArray from 'lodash/isArray';

import { createReactRenderer } from 'root/apps/helpers/react-renderer';

import CancelPreAuthModal from './CancelPreAuthModal';
import {
  CAPTURE_TOLERANCE_MULTIPLIER,
  CAPTURE_TOLERANCE_PERCENT,
  MIN_CAPTURE_AMOUNT,
} from './constants';
import useCustomerPreauths from './hooks/useCustomerPreauths';
import useGatewayParameters from './hooks/useGatewayParameters';
import OldestPreauth from './OldestPreauth';
import OtherPreauths from './OtherPreauths';
import PreAuthInfoBanner from './PreAuthInfoBanner';
import PreAuthSidebar from './PreAuthSidebar';
import { cancelPreauth } from './services/cancelService';
import { capturePayment, completeSale, PaymentTransaction } from './services/captureService';
import { CaptureLineData } from './types';

interface RegisterCustomerPreauthsProps {
  customerId: string;
}

const RegisterCustomerPreauths: React.FC<RegisterCustomerPreauthsProps> = ({ customerId }) => {
  // Extract transaction_id from URL if present
  const currentTransactionId = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('transaction_id') || undefined;
  }, []);

  const { oldestPreauth, otherPreauths, isLoading, isError, error } = useCustomerPreauths(
    customerId,
    currentTransactionId
  );

  const { isTippingEnabled, isSurchargeEnabled } = useGatewayParameters();

  const [sidebarContainer, setSidebarContainer] = useState<HTMLElement | null>(null);
  const [captureAmounts, setCaptureAmounts] = useState<Record<string, number>>({});
  const [selectedLines, setSelectedLines] = useState<Record<string, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Find the sidebar container
  useEffect(() => {
    const findContainer = () => {
      const container = document.getElementById('register_customer_preauths_sidebar');
      if (container) {
        setSidebarContainer(container);
        return true;
      }
      return false;
    };

    // Try immediately
    if (!findContainer()) {
      // If not found, retry with a small delay (sidebar might load after main content)
      const timer = setTimeout(() => {}, 100);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, []);

  // Initialize capture amounts and selection state when preauth loads
  useEffect(() => {
    if (oldestPreauth?.sale?.SaleLines?.SaleLine) {
      const initialAmounts: Record<string, number> = {};
      const initialSelected: Record<string, boolean> = {};
      const saleLine = oldestPreauth.sale.SaleLines.SaleLine;
      if (isArray(saleLine)) {
        saleLine.forEach((line) => {
          initialAmounts[line.saleLineID] = 0;
          initialSelected[line.saleLineID] = false;
        });
      } else {
        initialAmounts[saleLine.saleLineID] = 0;
        initialSelected[saleLine.saleLineID] = false;
      }
      setCaptureAmounts(initialAmounts);
      setSelectedLines(initialSelected);
    }
  }, [oldestPreauth]);

  const handleCaptureAmountChange = (lineId: string, amount: number) => {
    setCaptureAmounts((prev) => ({
      ...prev,
      [lineId]: amount,
    }));
  };

  const handleLineSelectionChange = (lineId: string, isSelected: boolean) => {
    setSelectedLines((prev) => ({
      ...prev,
      [lineId]: isSelected,
    }));
  };

  const handleValidationChange = useCallback((lineId: string, isValid: boolean) => {
    setValidationErrors((prev) => ({
      ...prev,
      [lineId]: !isValid,
    }));
  }, []);

  const hasValidationErrors = useCallback(() => {
    return Object.values(validationErrors).some((hasError) => hasError);
  }, [validationErrors]);

  // Show flash notification when validation errors occur
  useEffect(() => {
    const hideWarning = () => {
      // @ts-ignore - window.hideNotifier is a global function
      window.hideNotifier('warning', 0);
    };

    if (!oldestPreauth) {
      hideWarning();
      return;
    }

    const totalCapture = Object.values(captureAmounts).reduce((sum, amount) => sum + amount, 0);
    const authorizedAmount = parseFloat(oldestPreauth.amount?.amount as any) || 0;
    const maxAllowedAmount = authorizedAmount * CAPTURE_TOLERANCE_MULTIPLIER;

    if (hasValidationErrors()) {
      // @ts-ignore - window.flashNotifier is a global function
      window.flashNotifier(
        'warning',
        `Capture amount exceeds the limit. Maximum allowed: <var>$${authorizedAmount.toFixed(
          2
        )}</var> + <var>${CAPTURE_TOLERANCE_PERCENT}%</var> = <var>$${maxAllowedAmount.toFixed(
          2
        )}</var>. Adjust your capture amount.`,
        15
      );
    } else if (totalCapture > 0 && totalCapture <= MIN_CAPTURE_AMOUNT) {
      // @ts-ignore - window.flashNotifier is a global function
      window.flashNotifier(
        'warning',
        `Capture amount must be greater than <var>$${MIN_CAPTURE_AMOUNT.toFixed(2)}</var>`,
        15
      );
    } else {
      hideWarning();
    }
  }, [hasValidationErrors, oldestPreauth, captureAmounts]);

  // Validate customerId - return early if invalid
  if (!customerId || customerId === '0' || customerId === '') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No customer selected. Please attach a customer to view preauthorizations.</p>
      </div>
    );
  }

  const getTotalCapture = () => {
    return Object.values(captureAmounts).reduce((sum, amount) => sum + amount, 0);
  };

  const getCaptureData = (): CaptureLineData[] => {
    return Object.entries(captureAmounts)
      .filter(([, amount]) => amount > 0)
      .map(([lineId, amount]) => ({
        lineId,
        amount,
      }));
  };

  const handleCapture = async () => {
    const captureData = getCaptureData();
    const totalCapture = getTotalCapture();

    if (captureData.length === 0 || totalCapture <= 0) {
      // @ts-ignore - window.alertUser is a global function
      window.alertUser('Please select items to capture', 300);
      return;
    }

    if (!oldestPreauth) {
      // @ts-ignore - window.alertUser is a global function
      window.alertUser('No preauthorization found', 300);
      return;
    }

    try {
      // Show loading state
      // @ts-ignore - window.alertUser is a global function
      window.flashNotifier('working', 'Processing capture...', 300);

      // Capture the payment with line item data
      const captureResponse = await capturePayment({
        paymentId: oldestPreauth.paymentId,
        totalAmount: totalCapture,
        saleId: oldestPreauth.saleRef,
        lineItems: captureData, // Send line item capture amounts
      });

      // Check if capture was successful
      if (captureResponse.Payment.status !== 'approved') {
        // Check for canceled capture transaction to provide a better error message
        const transactions = captureResponse.Payment.Transactions?.PaymentTransaction;
        const captureTransaction = (() => {
          if (Array.isArray(transactions)) {
            return transactions.find(
              (t: PaymentTransaction) => t.gatewayTransactionType === 'capture'
            );
          }
          if (transactions?.gatewayTransactionType === 'capture') {
            return transactions;
          }
          return null;
        })();

        if (captureTransaction?.status === 'cancelled') {
          throw new Error(
            'The Preauthorization could not be captured. It may have expired or been cancelled. Please try a new payment.'
          );
        }
        throw new Error(`Capture failed with status: ${captureResponse.Payment.status}`);
      }

      // Complete the sale using the proper register workflow
      // The backend will load fresh transaction data from the database when completing
      // @ts-ignore - window.flashNotifier is a global function
      window.flashNotifier('working', 'Completing sale...', 300);
      await completeSale();
    } catch (error: any) {
      // Clear the loading notification
      // @ts-ignore - window.hideNotifier is a global function
      window.hideNotifier('working', 0);

      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to capture payment';
      // @ts-ignore - window.alertUser is a global function
      window.alertUser(`Error: ${errorMessage}`, 300);
    }
  };

  const handleCancelClick = () => {
    if (!oldestPreauth) {
      // @ts-ignore - window.alertUser is a global function
      window.alertUser('No preauthorization found', 300);
      return;
    }
    setIsCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!oldestPreauth) {
      return;
    }

    try {
      // Show loading state
      // @ts-ignore - window.flashNotifier is a global function
      window.flashNotifier('working', 'Cancelling preauthorization...', 300);

      // Cancel the pre-authorization
      const cancelResponse = await cancelPreauth({
        paymentId: oldestPreauth.paymentId,
        cancelReason: 'user_cancelled',
      });

      // Check if cancel was successful
      if (cancelResponse.Payment.status !== 'cancelled') {
        throw new Error(`Cancel failed with status: ${cancelResponse.Payment.status}`);
      }

      // Clear the loading notification
      // @ts-ignore - window.hideNotifier is a global function
      window.hideNotifier('working', 0);

      // The modal will show the success step
      // Don't close the modal or reload yet - let the user see the success message
    } catch (error: any) {
      // Clear the loading notification
      // @ts-ignore - window.hideNotifier is a global function
      window.hideNotifier('working', 0);

      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to cancel preauthorization';
      // @ts-ignore - window.alertUser is a global function
      window.alertUser(`Error: ${errorMessage}`, 300);
      // Close the modal on error
      setIsCancelModalOpen(false);
      throw error; // Re-throw to prevent modal from showing success
    }
  };

  const handleCancelModalClose = () => {
    setIsCancelModalOpen(false);
  };

  const handleCancelSuccess = () => {
    // After successful cancellation, switch back to the sale view
    // @ts-ignore - window.merchantos is a global object
    if (window.merchantos?.register?.changeView) {
      window.merchantos.register.changeView('sale');
    }
  };

  const handlePrintReceipt = () => {
    // Trigger the print receipt functionality using SaleReceipt template with preauth_state
    // @ts-ignore - window.merchantos is a global object
    if (window.merchantos?.print?.printUrl && oldestPreauth) {
      const saleId = Number(oldestPreauth.saleRef);
      if (!isNaN(saleId)) {
        // @ts-ignore - window.merchantos is a global object
        const systemCustomerId = window.merchantos.session.getSystemCustomerID();
        const url = `/API/Account/${systemCustomerId}/DisplayTemplate/Sale/${saleId}.html?template=SaleReceipt&preauth_state=cancelled&print=1&page_width=auto&page_height=2000mm&job_type=receipt`;
        window.merchantos.print.printUrl({ url, pageType: 'receipt' });
      }
    }
  };

  // Render main content based on state
  let mainContent;

  if (isLoading) {
    mainContent = (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading Preauthorizations...</p>
      </div>
    );
  } else if (isError) {
    mainContent = (
      <div style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
        <p>{error || 'Failed to load Preauthorizations. Please try again.'}</p>
      </div>
    );
  } else if (!oldestPreauth) {
    mainContent = (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No Preauthorizations found.</p>
      </div>
    );
  } else {
    mainContent = (
      <div id="customer-preauths-container" data-customer-id={customerId}>
        <PreAuthInfoBanner
          saleRef={oldestPreauth.saleRef}
          captureDeadline={oldestPreauth.captureDeadline}
        />
        <OldestPreauth
          preauth={oldestPreauth}
          captureAmounts={captureAmounts}
          selectedLines={selectedLines}
          onCaptureAmountChange={handleCaptureAmountChange}
          onLineSelectionChange={handleLineSelectionChange}
          onValidationChange={handleValidationChange}
          totalCapture={getTotalCapture()}
        />
        <OtherPreauths preauths={otherPreauths} />
      </div>
    );
  }

  return (
    <>
      {/* Main view content */}
      {mainContent}

      {/* Sidebar content - rendered via portal (only when we have data) */}
      {sidebarContainer &&
        oldestPreauth &&
        createPortal(
          <PreAuthSidebar
            preauth={oldestPreauth}
            totalCapture={getTotalCapture()}
            onCapture={handleCapture}
            onCancel={handleCancelClick}
            hasValidationErrors={hasValidationErrors()}
            isTippingEnabled={isTippingEnabled}
            isSurchargeEnabled={isSurchargeEnabled}
          />,
          sidebarContainer
        )}

      {/* Cancel Preauthorization Modal */}
      {oldestPreauth && (
        <CancelPreAuthModal
          isOpen={isCancelModalOpen}
          saleNumber={oldestPreauth.saleRef}
          onConfirm={handleCancelConfirm}
          onClose={handleCancelModalClose}
          onPrintReceipt={handlePrintReceipt}
          onSuccess={handleCancelSuccess}
        />
      )}
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: RegisterCustomerPreauths,
  selector: '[data-react-root="RegisterCustomerPreauths"]',
});

// Mount the component when it appears in the DOM
// This is needed because the register view is loaded dynamically via AJAX
let isMounted = false;

const observer = new MutationObserver(() => {
  const element = document.querySelector('[data-react-root="RegisterCustomerPreauths"]');

  if (element && !element.hasChildNodes() && !isMounted) {
    // Element exists and needs mounting
    RenderedComponent.mount();
    isMounted = true;
  } else if (!element && isMounted) {
    // Element was removed, clean up for next mount
    RenderedComponent.unmount();
    isMounted = false;
  }
});

// Cleanup observer on page unload
window.addEventListener('beforeunload', () => {
  observer.disconnect();
});

// Start observing when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    observer.observe(document.body, { childList: true, subtree: true });
    // Try the initial mount
    const element = document.querySelector('[data-react-root="RegisterCustomerPreauths"]');
    if (element && !element.hasChildNodes()) {
      RenderedComponent.mount();
      isMounted = true;
    }
  });
} else {
  observer.observe(document.body, { childList: true, subtree: true });
  // Try the initial mount
  const element = document.querySelector('[data-react-root="RegisterCustomerPreauths"]');
  if (element && !element.hasChildNodes()) {
    RenderedComponent.mount();
    isMounted = true;
  }
}

export default RegisterCustomerPreauths;
